import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import {
	DateFieldConfiguration,
	FieldConfiguration,
	FrontendExternalDataCallConfiguration,
	GroupFieldConfiguration,
	LookupFieldConfiguration,
	NumberFieldConfiguration,
	SelectFieldConfiguration,
	StringFieldConfiguration,
} from '../../../api/ConfigApi';
import { MediaResponse } from '../../../types/media';
import {
	FieldGroupCreateRequest,
	FieldGroupsByLocatorMap,
	FieldValues,
	FieldValuesMap,
} from '../../../types/policy';
import FieldLabel from '../../fields/FieldLabel';
import DateField from './DateField';
import GroupField from './GroupField';
import LookupField from './LookupField';
import MediaField from './MediaField';
import ReactSelect, { SelectValue } from './ReactSelect';
import { EMAIL_REGEX } from './validations';

interface Props {
	field: FieldConfiguration;
	fieldConfigurations?: FieldConfiguration[];
	value?: FieldValues;
	isDisabled: boolean;
	updateFieldValue: (
		field: FieldConfiguration,
		value: FieldValues,
		usesExternalDataCall?: boolean,
	) => void;
	updateGroupFieldValue?: (
		name: string,
		values: FieldValuesMap,
		index: number,
		fieldGroupLocator?: string,
	) => void;
	onAddFieldGroup?(fieldName: string): void;
	onChangeFieldGroup?(
		name: string,
		locator: string,
		values: FieldValuesMap,
		index?: number,
	): void;
	removeFieldGroup?(locator: string, index: number, name: string): void;
	updateOnChange?: boolean;
	externalDataCalls?: FrontendExternalDataCallConfiguration[];
	fieldGroupsByLocator?: FieldGroupsByLocatorMap;
	tabularDatasourcesHandler?: any;
	userHasWriteAccess?: boolean;
	allValues: FieldValuesMap;
	currentFieldValues?: FieldValuesMap; // current field values are the pending changes that the user is making
	addFieldGroups?: FieldGroupCreateRequest[];
	groupIndex?: string; // for group field unique keys
	mediaByLocator?: { [locator: string]: MediaResponse };
	configVersion?: string;
}

const useStyles = makeStyles((theme) => ({
	disabledInput: {
		'& .MuiInputBase-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
		'& .MuiSelect-select:focus': {
			backgroundColor: 'white',
		},
	},
	formControl: {
		'& .MuiFormHelperText-root': {
			backgroundColor: 'white',
		},
	},
}));

/**
 * If field is repeatable, field.type === 'group', or field.type === 'media' then the value prop should be
 * an array
 */
const Field = (props: Props) => {
	const classes = useStyles();
	const {
		field,
		value,
		isDisabled,
		updateFieldValue,
		onAddFieldGroup,
		removeFieldGroup,
		updateGroupFieldValue,
		addFieldGroups,
		groupIndex,
		currentFieldValues,
		mediaByLocator,
		externalDataCalls,
		configVersion,
	} = props;

	const selectDefaultOneOption =
		field.type === 'select' && field.values.length === 1 && !field.optional;
	const [initialized, setInitialized] = React.useState(false); // used to track if single option select value has been set or not
	const [val, setVal] = React.useState(value);

	const fieldExternalDataCall =
		externalDataCalls &&
		externalDataCalls.find((e) => e.triggerField === field.name);
	const hasExternalDataCall = !!fieldExternalDataCall;

	// update state when value prop changes
	useEffect(() => {
		setVal(value);
	}, [value]);

	const update = () => {
		updateFieldValue(field, val, hasExternalDataCall);
	};

	const updateMedia = (v: FieldValues) => {
		updateFieldValue(field, v, hasExternalDataCall);
	};

	const updateSelect = useCallback(
		(selectedVal: FieldValues) => {
			setVal(selectedVal);
			const update = () => {
				updateFieldValue(field, selectedVal, hasExternalDataCall);
			};
			update();
		},
		[setVal, updateFieldValue, field, hasExternalDataCall],
	);

	useEffect(() => {
		// on page load, if Select field does not have default value yet and only has one option, set its state with said value
		if (
			(!value || !value.length || !value[0]) &&
			!initialized &&
			selectDefaultOneOption &&
			!isDisabled
		) {
			//  && value[0] !== field.values[0]
			updateSelect([field.values[0]]);
			setInitialized(true);
		}
	}, [
		initialized,
		selectDefaultOneOption,
		value,
		field,
		updateSelect,
		setInitialized,
	]);

	const setValue = (v: string) => {
		// this function is called for every string, number, date and email field's onChange
		setVal([v]);
		// if the field is not a date field and uses an external data call
		// we don't want to send an update. The update will be sent when the onBlur property is called
		if (field.type === 'date' || !hasExternalDataCall) {
			updateFieldValue(field, [v], hasExternalDataCall);
		}
	};

	const style = {
		display: 'flex',
		flexDirection: 'column',
	} as React.CSSProperties;

	const formGroupStyle = {
		justifyContent: 'flex-start',
		width: '100%',
	} as React.CSSProperties;

	// if field is disabled, get value from props, otherwise get from state
	const currVal = isDisabled ? value : val;
	const fieldId = groupIndex ? `${field.name}-${groupIndex}` : `${field.name}`;
	switch (field.type) {
		case 'string':
			const hasCurrVal = currVal?.[0] && currVal[0] !== '';
			const regexVal = (field as StringFieldConfiguration).regex;
			const matchesRegex =
				hasCurrVal && regexVal ? currVal[0].match(regexVal) : true;
			return (
				<FormControl style={formGroupStyle}>
					<div style={style}>
						<FieldLabel
							required={!field.optional}
							title={field.title}
							id={`${fieldId}-label`}
						/>
						<TextField
							className={classes.disabledInput}
							id={fieldId}
							value={currVal[0] || ''}
							multiline={(field as StringFieldConfiguration).multiline}
							maxRows={4}
							error={!matchesRegex}
							helperText={
								!matchesRegex ? `Must match regex: ${regexVal}` : null
							}
							disabled={isDisabled}
							onChange={(e) => setValue(e.target.value)}
							onBlur={update}
						/>
					</div>
				</FormControl>
			);
		case 'number':
			const numberField = field as NumberFieldConfiguration;
			let helperText;

			if (currVal?.[0]) {
				const currValFloat = parseFloat(currVal[0]);
				if (
					currValFloat < parseFloat(numberField.minimum) ||
					currValFloat > parseFloat(numberField.maximum)
				) {
					helperText = `Must be between ${numberField.minimum} - ${numberField.maximum}`;
				} else if (!!`${currVal[0]}`.split('.')[1]) {
					const isDecimalValid =
						`${currVal[0]}`.split('.')[1].length <=
						parseInt(numberField.decimalPlaces, 10);

					if (!isDecimalValid) {
						helperText = `Must have ${numberField.decimalPlaces} or fewer decimal places`;
					}
				}
			}
			return (
				<FormControl style={formGroupStyle}>
					<div style={style}>
						<FieldLabel
							required={!field.optional}
							title={field.title}
							id={`${fieldId}-label`}
						/>
						<TextField
							className={classes.disabledInput}
							id={fieldId}
							value={currVal[0] || ''}
							disabled={isDisabled}
							error={!!helperText}
							helperText={helperText}
							type='number'
							onChange={(e) => setValue(e.target.value)}
							onBlur={update}
						/>
					</div>
				</FormControl>
			);
		case 'date':
			return (
				<DateField
					key={`${currVal[0]}-${fieldId}`}
					field={field as DateFieldConfiguration}
					value={currVal[0] || ''}
					required={!field.optional}
					onChange={setValue}
					isDisabled={isDisabled}
				/>
			);
		case 'select':
			const isSelectRepeatable =
				field.type === 'select' &&
				(field as SelectFieldConfiguration).repeatable;
			let selectValue = {
				value: currVal?.[0] || '',
				label: currVal?.[0] || '',
			} as SelectValue | SelectValue[];
			let isMulti = false;

			if (isSelectRepeatable) {
				isMulti = true;
				selectValue = currVal
					? currVal.map((v) => ({ value: v || '', label: v || '' }))
					: [];
				selectValue = selectValue.filter((v) => !!v?.value); // remove blank values for multi select
			}

			const options = field.values.map((v) => ({
				value: v || '',
				label: v || '',
			}));

			const onSelectChange = (opt: SelectValue | SelectValue[]) => {
				const newSelectValue = isSelectRepeatable
					? (opt as SelectValue[]).map((opt: SelectValue) => opt.value)
					: [(opt as SelectValue).value];

				updateSelect(newSelectValue);
			};

			return (
				<FormControl style={formGroupStyle}>
					<div style={style}>
						<FieldLabel
							required={!field.optional}
							title={field.title}
							id={`${fieldId}-label`}
						/>
						<ReactSelect
							id={fieldId}
							className={classes.disabledInput}
							value={selectValue}
							options={options}
							onChange={onSelectChange}
							isDisabled={isDisabled}
							isMulti={isMulti}
							includeEmptyValue={field.optional ? field.optional : false} // only include empty value is optional
						/>
					</div>
				</FormControl>
			);
		case 'email':
			const emailMatchesRegex =
				currVal?.[0] && EMAIL_REGEX ? currVal[0].match(EMAIL_REGEX) : true;
			return (
				<FormControl style={formGroupStyle}>
					<div style={style}>
						<FieldLabel
							required={!field.optional}
							title={field.title}
							id={`${fieldId}-label`}
						/>
						<TextField
							className={classes.disabledInput}
							id={fieldId}
							value={currVal[0]}
							disabled={isDisabled}
							onChange={(e) => setValue(e.target.value)}
							onBlur={update}
							type='email'
							helperText={emailMatchesRegex ? '' : 'Invalid email address'}
							error={!emailMatchesRegex}
						/>
					</div>
				</FormControl>
			);
		case 'media':
			// value should be array
			return (
				<MediaField
					labelId={`${fieldId}-label`}
					field={field}
					value={currVal}
					updateMedia={updateMedia}
					mediaByLocator={mediaByLocator}
					disabled={isDisabled}
					required={!field.optional}
				/>
			);
		case 'group':
			// value should be array
			return (
				<GroupField
					labelId={`${fieldId}-label`}
					field={field as GroupFieldConfiguration}
					externalDataCalls={props.externalDataCalls}
					fieldGroupLocators={currVal}
					fieldGroupsByLocator={props.fieldGroupsByLocator}
					isEditable={!isDisabled}
					updateGroupFieldValue={updateGroupFieldValue}
					addFieldGroup={onAddFieldGroup}
					removeFieldGroup={removeFieldGroup}
					tabularDatasourcesHandler={props.tabularDatasourcesHandler}
					userHasWriteAccess={props.userHasWriteAccess}
					addFieldGroups={addFieldGroups}
					currentFieldValues={currentFieldValues}
					mediaByLocator={mediaByLocator}
					configVersion={configVersion}
				/>
			);
		case 'lookup':
			const fieldValues = _.merge(
				{},
				props.allValues,
				props.currentFieldValues,
			);
			return (
				<LookupField
					labelId={`${fieldId}-label`}
					field={field as LookupFieldConfiguration}
					value={currVal?.[0] || ''}
					isDisabled={isDisabled}
					userHasWriteAccess={props.userHasWriteAccess}
					allValues={fieldValues}
					style={style}
					updateLookup={updateSelect}
					configVersion={configVersion}
				/>
			);
		default:
			return (
				<FormControl style={formGroupStyle}>
					<div style={style}>
						<FieldLabel
							required={!field.optional}
							title={field.title}
							id={`${fieldId}-label`}
						/>
						<TextField
							className={classes.disabledInput}
							id={fieldId}
							value={currVal?.[0] || ''}
							disabled={isDisabled}
							onChange={(e) => setValue(e.target.value)}
							onBlur={update}
						/>
					</div>
				</FormControl>
			);
	}
};

export default Field;
